<template>
    <card title="" :isFullPage="true">
        <data-table entity-name="verified computer" entity-name-plural="verified computers" :columns="columns" :rows="rows"
            :total-row-count="totalRowCount" selection-key="hardware_id" :action-in-progress="actionsInProgress > 0"
            @queryChanged="onQueryChanged">

            <template v-slot:actions="props">
                <import-action :selection-count="props.selectionCount" :disabled="actionsInProgress > 0"
                    :entity-name="props.entityName" :entity-name-plural="props.entityNamePlural"
                    import-resource="hardwares-verified"></import-action>
                <export-action v-bind="props" export-resource="hardwares-verified"></export-action>
                <delete-action v-if="isSuperUser || isStaffUser || isPartner || isManager" v-bind="props" @deleteAction="onDelete"></delete-action>
                <print-action v-bind="props" @printAction="onPrint"></print-action>
            </template>
        </data-table>
    </card>
</template>
<script>
import { mapGetters } from 'vuex';
import { notify } from '../services/notify';

import api from '../services/api';
import print from '../components/DataTable/print';
import imports from '../components/DataTable/Actions/imports';
import DataTable from '../components/DataTable/DataTable.vue';
import DeleteAction from '../components/DataTable/Actions/DeleteAction.vue';
import ExportAction from '../components/DataTable/Actions/ExportAction.vue';
import ImportAction from '../components/DataTable/Actions/ImportAction.vue';
import PrintAction from '../components/DataTable/Actions/PrintAction.vue';


const chassisTypes = [
    "Other",
    "Unknown",
    "Desktop",
    "Low Profile Desktop",
    "Pizza Box",
    "Mini Tower",
    "Tower",
    "Portable",
    "Laptop",
    "Notebook",
    "Hand Held",
    "Docking Station",
    "All In One",
    "Sub Notebook",
    "Space-saving",
    "Lunch Box",
    "Main Server Chassis",
    "Expansion Chassis",
    "Sub Chassis",
    "Bus Expansion Chassis",
    "Peripheral Chassis",
    "RAID Chassis",
    "Rack Mount Chassis",
    "Sealed-case PC",
    "Multi-system",
    "CompactPCI",
    "AdvancedTCA",
    "Blade",
    "Blade Enclosing",
    "Tablet",
    "Convertible",
    "Detachable",
    "IoT Gateway",
    "Embedded PC",
    "Mini PC",
    "Stick PC"
];

export default {
    name: 'ComputersVerified',
    components: {
        DataTable,
        DeleteAction,
        ExportAction,
        ImportAction,
        PrintAction,
    },
    data() {
        return {
            columns: [],
            baseColumns: [
                { key: 'details', type: 'action', action: this.viewComputer, actionIcon: 'desktop', visible: true },
                { key: 'created_at', type: 'datetime', visible: true },
                { key: 'updated_at', type: 'datetime' },
                { key: 'uploaded_at', type: 'datetime' },
                { key: 'software_version' },
                { key: 'report_id', label: 'Report ID'},
                { key: 'batch_name' },

                { key: 'system_vendor', visible: true },
                { key: 'system_model', visible: true },
                { key: 'system_uuid', label: 'System UUID', visible: true },
                { key: 'system_serial', visible: true },
                { key: 'system_family' },
                { key: 'system_sku_number', label: 'System SKU number' },

                { key: 'chassis_type', type: 'category', categories: chassisTypes },
                { key: 'chassis_asset_tag' },
                { key: 'chassis_serial', },

                { key: 'cpu_vendor', label: 'CPU vendor' },
                { key: 'cpu_model', label: 'CPU model' },
                { key: 'cpu_speed_display', proxy_key: 'cpu_speed_mhz', label: 'CPU speed', type: 'number' },
                { key: 'cpu_l2_cache', label: 'CPU L2 cache' },
                { key: 'cpu_l3_cache', label: 'CPU L3 cache' },
                { key: 'cpu_voltage', label: 'CPU voltage' },
                { key: 'cpu_external_clock', label: 'CPU external clock' },
                { key: 'cpu_stepping', label: 'CPU stepping' },
                { key: 'cpu_number_of_cores', label: 'CPU number of cores', type: 'number' },
                { key: 'cpu_threads_per_core', label: 'CPU threads per core', type: 'number' },
                { key: 'cpu_cores_per_socket', label: 'CPU cores per socket', type: 'number' },
                { key: 'cpu_number_of_sockets', label: 'CPU number of sockets', type: 'number' },

                { key: 'bios', label: 'BIOS' },
                { key: 'computrace', label: 'Computrace' },
                { key: 'baseboard_vendor' },
                { key: 'baseboard_model' },
                { key: 'baseboard_serial' },

                { key: 'primary_display_resolution', label: 'Display resolution' },
                { key: 'primary_display_size', label: 'Display size' },
                { key: 'usb_version', label: 'USB version', type: 'category',
                    categories: ['1.0', '1.1', '2.0', '3.0', '3.1', '3.2']
                },
                {
                    key: 'memory_total_capacity_display', proxy_key: 'memory_total_capacity_bytes', label: 'Memory capacity', type: 'number',
                    units: [
                        { label: 'MB', fromUser: v => v * 2 ** 20, toUser: v => v / 2 ** 20 },
                        { label: 'GB', fromUser: v => v * 2 ** 30, toUser: v => v / 2 ** 30 },
                        { label: 'TB', fromUser: v => v * 2 ** 40, toUser: v => v / 2 ** 40 },
                    ]
                },
                {
                    key: 'memory', type: 'table', columns: [
                        { key: 'slot' },
                        { key: 'vendor' },
                        { key: 'model' },
                        { key: 'capacity_display', proxy_key: 'capacity_bytes', label: 'Capacity', type: 'number' },
                        { key: 'description' }
                    ]
                },
                {
                    key: 'network', type: 'table', columns: [
                        { key: 'vendor' },
                        { key: 'model' },
                        { key: 'type' },
                        { key: 'mac' }
                    ]
                },
                {
                    key: 'storage', type: 'table', columns: [
                        { key: 'vendor' },
                        { key: 'model' },
                        { key: 'description' }
                    ]
                },
                {
                    key: 'disk', type: 'table', columns: [
                        { key: 'details', type: 'action', action: this.viewDisk, actionIcon: 'hdd', visible: true },
                        { key: 'vendor' },
                        { key: 'model' },
                        { key: 'serial' },
                        { key: 'capacity_display', proxy_key: 'capacity_bytes', type: 'number' },
                        { key: 'verify_result_display', label: 'Verification result' }
                    ], visible: true
                },
                {
                    key: 'optical', type: 'table', columns: [
                        { key: 'vendor' },
                        { key: 'model' },
                        { key: 'description' }
                    ]
                },
                {
                    key: 'video', type: 'table', columns: [
                        { key: 'vendor' },
                        { key: 'model' }
                    ]
                },
                {
                    key: 'audio', type: 'table', columns: [
                        { key: 'vendor' },
                        { key: 'model' }
                    ]
                },
                {
                    key: 'port', type: 'table', columns: [
                        { key: 'type' },
                        { key: 'designator' },
                        { key: 'connector' }
                    ]
                },
                {
                    key: 'peripheral', type: 'table', columns: [
                        { key: 'type' },
                        { key: 'description' }
                    ]
                },
                {
                    key: 'battery', type: 'table', columns: [
                        { key: 'vendor' },
                        { key: 'model' },
                        { key: 'serial' },
                        { key: 'technology' },
                        { key: 'energy_full_design', type: 'number' },
                        { key: 'energy_full', type: 'number' },
                        { key: 'life_remaining_percent', type: 'number' }
                    ]
                },
                {
                    key: 'extra', type: 'table', columns: [
                        { key: 'key' },
                        { key: 'value' }
                    ], visible: true
                },
                {
                    key: 'test_result', type: 'table', columns: [
                        { key: 'name_display', label: 'Name' },
                        { key: 'result_display', label: 'Result' },
                        { key: 'notes' }
                    ]
                },
            ],
            rows: [],
            totalRowCount: 0,
            lastQuery: null,
            actionsInProgress: 0,
            savedTableName: null,
        }
    },
    async created() {
        await this.$store.cache.dispatch('user/fetchMe');
        if (this.isSuperUser || this.isStaffUser || this.isPartner) {
            this.columns = [{ key: 'organization.name', label: 'Organization', visible: true }, { key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else if (this.isManager) {
            this.columns = [{ key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else {
            this.columns = this.baseColumns;
        }
    },
    methods: {
        async refreshQuery() {
            if (this.lastQuery !== null) {
                await this.onQueryChanged(this.lastQuery);
            }
        },
        async onQueryChanged(query) {
            this.lastQuery = query;
            try {
                const response = await api.getWithParams('/hardwares-verified/', query);
                this.rows = response.data.data;
                this.totalRowCount = response.data.pagination.count;
            }
            catch(err) {
                this.rows = [];
                this.totalRowCount = 0;
                notify().error('Error fetching reports.');
            }
        },
        async onDelete(selectOrFilter) {
            this.actionsInProgress++;
            try {
                const response = await api.delete('/hardwares-verified/', selectOrFilter);
                await this.refreshQuery();
            }
            catch(err) {
                notify().error('Error deleting reports.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async onPrint(selectOrFilter, rowConverter) {
            this.actionsInProgress++;
            try {
                const response = await api.postJSON('/hardwares-verified/select', selectOrFilter);
                const rows = response.data;
                await print.pdf(rowConverter(rows));
            }
            catch(err) {
                notify().error('Error printing a report.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async viewComputer(row) {
            this.$router.push(`/verified/computers/${row.hardware_id}`);
        },
        async viewDisk(row) {
            this.$router.push(`/verified/disks/${row.device_id}`);
        },
    },
    computed: {
        ...mapGetters('user', ['isSuperUser', 'isStaffUser', 'isPartner', 'isManager']),
    },
}
</script>``
